import { useEffect, useState } from "react";
import { ImageOff, ZoomIn } from "lucide-react";
import type { PublicAppTemplate } from "@/lib/app-templates";

export function PublicTemplatePreview({
  template,
  onExpand,
}: {
  template: PublicAppTemplate;
  onExpand: () => void;
}) {
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [template.preview_image_url]);

  const showImage = Boolean(template.preview_image_url) && !previewError;

  if (!showImage) {
    return (
      <button
        type="button"
        onClick={onExpand}
        aria-label={`Open preview of ${template.name}`}
        aria-haspopup="dialog"
        className="relative flex aspect-[16/10] w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-border bg-muted/50 px-6 text-center text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <ImageOff className="h-5 w-5" aria-hidden="true" />
        Preview image
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={`Enlarge preview of ${template.name}`}
      aria-haspopup="dialog"
      className="group relative block aspect-[16/10] w-full overflow-hidden rounded-lg border border-border bg-muted/50 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <img
        src={template.preview_image_url ?? undefined}
        alt={`${template.name} App preview`}
        className="h-full w-full bg-white object-cover transition-transform duration-200 group-hover:scale-[1.01]"
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setPreviewError(true)}
      />
      <div className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-slate-950/80 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm">
        <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
        Enlarge
      </div>
    </button>
  );
}
